import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Token balance for the active org. Balance rides `subscription.tokenBalance`
 * (delivered by AuthContext with zero extra reads); the monthly allowance + the
 * unlimited flag come from a single plan-doc read.
 */
export interface TokenBalance {
  /** Current spendable balance. */
  balance: number | null;
  /** Non-resetting balance from one-time add-on purchases (null when absent). Never expires. */
  purchasedTokens: number | null;
  /** Monthly allowance for the plan (null when unlimited). */
  grant: number | null;
  /** Tokens consumed this period (grant - balance, clamped ≥ 0). */
  used: number | null;
  unlimited: boolean;
  /** 0–100 of the monthly allowance consumed (null when unlimited / unknown). */
  percentUsed: number | null;
  /** Plan seat limit (null when unlimited / unknown). */
  maxUsers: number | null;
  plan: string | null;
  loading: boolean;
}

export function useTokenBalance(): TokenBalance {
  const { activeOrganization } = useAuth();
  const [state, setState] = useState<{ grant: number | null; unlimited: boolean; maxUsers: number | null; loading: boolean }>({
    grant: null,
    unlimited: false,
    maxUsers: null,
    loading: true,
  });

  const plan = activeOrganization?.subscription?.plan ?? null;
  const balance =
    typeof activeOrganization?.subscription?.tokenBalance === 'number'
      ? activeOrganization.subscription.tokenBalance
      : null;
  const purchasedTokens =
    typeof activeOrganization?.subscription?.purchasedTokenBalance === 'number'
      ? activeOrganization.subscription.purchasedTokenBalance
      : null;

  useEffect(() => {
    if (!plan) {
      setState({ grant: null, unlimited: false, maxUsers: null, loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true }));
      try {
        const snap = await getDoc(doc(db, 'subscription_plans', plan));
        if (cancelled) return;
        const ent = snap.exists() ? (snap.data() as any).entitlements : null;
        const unlimited = ent?.tokensUnlimited === true;
        const grant = unlimited ? null : typeof ent?.tokensPerMonth === 'number' ? ent.tokensPerMonth : null;
        // entitlements.maxUsers: 0 = unlimited → null
        const rawMaxUsers = typeof ent?.maxUsers === 'number' ? ent.maxUsers : null;
        const maxUsers = rawMaxUsers && rawMaxUsers > 0 ? rawMaxUsers : null;
        setState({ grant, unlimited, maxUsers, loading: false });
      } catch (err) {
        console.error('[useTokenBalance] failed to load plan:', err);
        if (!cancelled) setState({ grant: null, unlimited: false, maxUsers: null, loading: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plan]);

  const { grant, unlimited, maxUsers, loading } = state;
  const used = grant != null && balance != null ? Math.max(0, grant - balance) : null;
  const percentUsed =
    !unlimited && grant != null && grant > 0 && used != null ? Math.min(100, (used / grant) * 100) : null;

  return { balance, purchasedTokens, grant, used, unlimited, percentUsed, maxUsers, plan, loading };
}
